import { useLayoutEffect } from "react";
import ResizeObserver from "resize-observer-polyfill";

export const AUTO_FIT_OPTIONS = {
  alignTop: true,
  topPadding: 8,
  sidePadding: 8,
  bottomPadding: 8,
};

export const applyAutoFit = (canvasRef, options = AUTO_FIT_OPTIONS) => {
  const handler = canvasRef?.current?.handler;
  const canvas = handler?.canvas;
  if (!handler?.zoomHandler || !canvas) return false;

  const width = canvas.getWidth();
  const height = canvas.getHeight();
  if (!width || !height) return false;

  const objects = canvas.getObjects();
  if (!objects?.length) return false;

  // Limita padding para pantallas pequenas y evita que el mapa quede encogido.
  const maxSidePadding = Math.max(Math.floor(width * 0.03), 4);
  const maxVerticalPadding = Math.max(Math.floor(height * 0.03), 4);

  const normalizedOptions = {
    ...options,
    sidePadding: Math.min(options.sidePadding ?? AUTO_FIT_OPTIONS.sidePadding, maxSidePadding),
    topPadding: Math.min(options.topPadding ?? AUTO_FIT_OPTIONS.topPadding, maxVerticalPadding),
    bottomPadding: Math.min(
      options.bottomPadding ?? AUTO_FIT_OPTIONS.bottomPadding,
      maxVerticalPadding
    ),
  };

  handler.zoomHandler.zoomToFit(normalizedOptions);
  return true;
};

export const useAutoFitOnContainerResize = (
  containerRef,
  canvasRef,
  isMapLoaded,
  {
    alignTop = AUTO_FIT_OPTIONS.alignTop,
    topPadding = AUTO_FIT_OPTIONS.topPadding,
    sidePadding = AUTO_FIT_OPTIONS.sidePadding,
    bottomPadding = AUTO_FIT_OPTIONS.bottomPadding,
    debounceMs = 120,
    hideUntilFirstFit = true,
  } = {}
) => {
  useLayoutEffect(() => {
    if (!isMapLoaded || !containerRef?.current) return;

    let timeoutId;
    let rafId;
    let retryTimeoutId;
    let initialFitIntervalId;
    let retryCount = 0;
    let lastAppliedSize = { width: 0, height: 0 };
    let pendingSize = { width: 0, height: 0 };
    let hasDoneFirstFit = false;
    let isRunningInitialFitSequence = true;
    const fitOptions = { alignTop, topPadding, sidePadding, bottomPadding };
    const canvasWrapper = containerRef.current.querySelector(".canvas-wrapper");

    if (hideUntilFirstFit && canvasWrapper) {
      canvasWrapper.style.opacity = "0";
      canvasWrapper.style.visibility = "hidden";
    }

    const showWrapperAfterFirstFit = () => {
      if (!hasDoneFirstFit && hideUntilFirstFit && canvasWrapper && !isRunningInitialFitSequence) {
        canvasWrapper.style.visibility = "visible";
        canvasWrapper.style.opacity = "1";
        hasDoneFirstFit = true;
      }
    };

    const fitToContainer = (useRaf = true) => {
      const run = () => {
        const didFit = applyAutoFit(canvasRef, fitOptions);
        if (didFit) {
          retryCount = 0;
          lastAppliedSize = { ...pendingSize };
          showWrapperAfterFirstFit();
          return;
        }

        // Algunos mapas terminan de cargar sus objetos después del primer ciclo.
        if (retryCount < 8) {
          retryCount += 1;
          clearTimeout(retryTimeoutId);
          retryTimeoutId = setTimeout(() => fitToContainer(true), 120);
        }
      };

      if (useRaf) {
        rafId = requestAnimationFrame(run);
        return;
      }

      run();
    };

    const scheduleFit = (width, height) => {
      pendingSize = { width, height };
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fitToContainer(true), debounceMs);
    };

    const observer = new ResizeObserver((entries) => {
      const { width = 0, height = 0 } = (entries[0] && entries[0].contentRect) || {};
      if (!width || !height) return;

      const deltaWidth = Math.abs(width - lastAppliedSize.width);
      const deltaHeight = Math.abs(height - lastAppliedSize.height);
      // Evita micro reajustes que causan parpadeo visual
      if (deltaWidth < 2 && deltaHeight < 2) return;

      scheduleFit(width, height);
    });

    observer.observe(containerRef.current);
    const initialRect = containerRef.current.getBoundingClientRect();
    pendingSize = { width: initialRect.width || 0, height: initialRect.height || 0 };
    if (pendingSize.width > 0 && pendingSize.height > 0) {
      // Secuencia corta inicial: algunos mapas terminan de acomodar objetos
      // unos ms despues de importarse; esto evita mostrar un fit parcial.
      fitToContainer(false);
      let initialAttempts = 1;
      initialFitIntervalId = setInterval(() => {
        initialAttempts += 1;
        fitToContainer(true);
        if (initialAttempts >= 8) {
          clearInterval(initialFitIntervalId);
          isRunningInitialFitSequence = false;
          showWrapperAfterFirstFit();
        }
      }, 120);
    } else {
      scheduleFit(pendingSize.width, pendingSize.height);
      // Si no hay dimensiones al inicio, liberamos visibilidad en fallback.
      timeoutId = setTimeout(() => {
        isRunningInitialFitSequence = false;
        showWrapperAfterFirstFit();
      }, 500);
    }

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(retryTimeoutId);
      clearInterval(initialFitIntervalId);
      if (rafId) cancelAnimationFrame(rafId);
      if (hideUntilFirstFit && canvasWrapper) {
        canvasWrapper.style.visibility = "";
        canvasWrapper.style.opacity = "";
      }
      observer.disconnect();
    };
  }, [
    isMapLoaded,
    containerRef,
    canvasRef,
    alignTop,
    topPadding,
    sidePadding,
    bottomPadding,
    debounceMs,
    hideUntilFirstFit,
  ]);
};

